import { Component, computed, inject, input, afterNextRender, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Faqs } from '../signup.types';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-faqs',
  imports: [
    NgbAccordionModule
  ],
  templateUrl: './faqs.component.html',
  styleUrl: './faqs.component.scss'
})
export class FaqsComponent {

  //#region Services

  private sanitizer = inject(DomSanitizer);

  //#endregion

  //#region I/O

  step = input.required<number>();

  //#endregion

  //#region Properties

  faqs = signal<Faqs[]>([]);

  faqsInView = computed(() => this.faqs().find(x => x.step === this.step())?.data ?? []);

  //#endregion

  //#region Constructor

  constructor() {
    afterNextRender(() => {
      this.faqs.set([
        {
          step: 2,
          data: [
            {
              question: `How many people does a Farmbox serve?`,
              answer: this.#sanitizeHtml(`
               <p>
                 Farmbox Delivery offers various box sizes to accommodate different household needs:
               <p>
               <ul>
                <li>
                  <p>
                    <strong>
                      Wee FarmBox: Serves 1-2 people.
                    </strong>
                  </p>
                </li>
                <li>
                  <p>
                    <strong>
                      Regular FarmBox: Serves 2-3 people.
                    </strong>
                  </p>
                </li>
                <li>
                  <p>
                    <strong>
                      Large FarmBox: Serves 4-5 people.
                    </strong>
                  </p>
                </li>
                <li>
                  <p>
                    <strong>
                      Jumbo FarmBox: Ideal for families with a vegetarian diet.
                    </strong>
                  </p>
                </li>
                <li>
                  <p>
                    <strong>
                      Office Fruit FarmBox: Provides 50-60 servings, perfect for office settings.
                    </strong>
                  </p>
                </li>
               </ul>
               <p>
                Each box is designed to cater to different needs, and you can always adjust your selection or skip a week as needed.
               </p>
               `)
            },
            {
              question: `What makes shopping with FarmBox Delivery better than the supermarket or other delivery companies?`,
              answer: this.#sanitizeHtml(`
              <p>
                Lots of things! First, the food that you buy from FarmBox Delivery is much fresher and higher quality than the food you’d find at your traditional grocery store or other delivery companies since it’s not sitting around waiting to be purchased or traveling across the state to get to you. We do not keep inventory, we only order what we need for the boxes going out that week. This allows us to only deliver the very freshest groceries to you.
              </p>
              <p>
                With FarmBox Delivery, you can search for exactly what you want and find it right away—plus you can browse the aisles and discover new products. And our recipes give weekly inspiration so you’re never stuck trying to figure out what to make for dinner.
              </p>
               `)
            },
            {
              question: `Is your produce organic?`,
              answer: `
               <p>
                While not all our produce is USDA Certified Organic, we prioritize sourcing from local farmers who practice sustainable and often "beyond organic" methods. Many of our small-scale producers use Integrated Pest Management (IPM) and other eco-friendly practices but may not have formal organic certification due to the associated costs. When sourcing from out-of-state, we include USDA Organic and IPM options to ensure quality and sustainability.
               </p>
               `
            },
          ]
        },
        {
          step: 1,
          data: [
            {
              question: `How will I know when my delivery has arrived?`,
              answer: this.#sanitizeHtml(`
                <p>
                  Our drivers will send you a text once your delivery arrives!
                </p>
               `)
            },
            {
              question: `How do you deliver such fresh food?`,
              answer: this.#sanitizeHtml(`
                <p>
                  Farmbox Delivery partners directly with local Texas farmers and food makers, allowing us to receive their products faster than traditional grocery stores or other delivery services. This direct relationship means your food spends less time in transit, ensuring it arrives at your doorstep fresher and more flavorful. Additionally, we use thermal packaging for all cold items to maintain optimal freshness, especially if you're not home to receive the delivery.
                </p>
               `)
            },
            {
              question: `What if I need to pause my subscription?`,
              answer: this.#sanitizeHtml(`
                <p>
                  Easy! Just log into your account and click “Pause Subscription” next to the delivery you want to skip. Whether you’re traveling or just need a break, your Farmbox can wait.
                </p>
               `)
            },
          ]
        },
        {
          step: 0,
          data: [
            {
              question: `Am I locked into a contract?`,
              answer: `
               <p>
                 No contracts, no commitments. You can skip, cancel, or change your deliveries anytime before the weekly cutoff (typically Sunday at 5 PM). We only charge you for the weeks you order.
               </p>
               `
            },
            {
              question: `When can I customize my Farmbox?`,
              answer: `
               <p>
                You will be able to customize your Farmbox every week from Friday at 5pm to Sunday at 5pm. Every week we bring you the freshest selections from our curated Farmers and producers.
               </p>
               `
            }
          ]
        },
        {
          step: 3,
          data: [
            {
              question: `Will you charge my credit card automatically?`,
              answer: `
               <p>
                 You’ll only be charged for the deliveries you actually receive. Charges happen after the customization window closes for each order.
               </p>
               `
            },
            {
              question: `Am I locked into a contract?`,
              answer: `
               <p>
                No contracts, no commitments. You can skip, cancel, or change your deliveries anytime before the weekly cutoff (typically Sunday at 5 PM). We only charge you for the weeks you order.
               </p>
               `
            },
          ]
        }
      ]);
    })
  }

  //#endregion

  //#region Methods

  #sanitizeHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  //#endregion
}
